import useApiRequest from "@/lib/hooks/use-request";
import {
  UpdateUserHierarchyRequestDto,
  UserDetailsResponseDto,
  UserResponseDto,
  UserSelectableEntitiesResponseDto,
  UserSelectableEntityResponseDto,
} from "@/lib/interfaces/users";
import PageNotFound from "@/page-components/error/page-not-found";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/ui-components/ui/accordion";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Input } from "@/ui-components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import {
  AccordionContainer,
  AccordionContainerParent,
} from "@/ui-components/utils/menu-items/accordion-container-manager";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { Archive, ArchiveRestore, Edit2, Pencil, User, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import UserEditor from "../user-editor";

const UserItem = () => {
  const { id } = useParams();
  return <>{id ? <UserItemFetcher id={id} /> : <PageNotFound />}</>;
};

const UserItemFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<UserDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<UserDetailsResponseDto>(`settings/users/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: UserDetailsResponseDto) => <UserDetails setReload={setReload} item={loadedItem} />}
    />
  );
};

const UserDetails = ({
  item,
  setReload,
}: {
  item: UserDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [userItem, setUserItem] = useState<UserDetailsResponseDto>(item);
  const [isEditing, setIsEditing] = useState(false);
  const { apiRequest } = useApiRequest();
  const { setTitle } = useBreadcrumb();

  useEffect(() => {
    setTitle(userItem.firstName + " " + userItem.lastName);
  }, [userItem, setTitle]);

  const toggleArchive = async () => {
    await apiRequest(`settings/users/${userItem._id}/${userItem.isArchive ? "unarchive" : "archive"}`, "PATCH");
    setReload((prev) => !prev);
  };

  return (
    <div className="h-full">
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>{userItem.firstName + " " + userItem.lastName}</PageTitle>
          <div className="flex gap-2">
            <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
              <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
              {isEditing ? <X size={16} /> : <Edit2 size={16} />}
            </Button>
            <ConfirmationDialog
              onConfirm={toggleArchive}
              title="Benutzer archivieren"
              description="Möchten Sie den Benutzer wirklich archivieren?"
              variant={userItem.isArchive ? "default" : "destructive"}
            >
              <Button className="gap-2" variant={userItem.isArchive ? "default" : "destructive"}>
                <span>{userItem.isArchive ? "Wiederherstellen" : "Archivieren"}</span>
                {userItem.isArchive ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              </Button>
            </ConfirmationDialog>
          </div>
        </div>
      </PageTitleContainer>
      {isEditing ? (
        <div className="p-4 w-1/2 items-center m-auto">
          <UserEditor
            item={userItem}
            onSubmitDone={() => {
              setIsEditing(false);
              setReload((prev) => !prev);
            }}
          />
        </div>
      ) : (
        <AccordionContainerParent>
          <AccordionContainer>
            <Accordion type="multiple" className="space-y-4">
              <AccordionItem value="persons" className="border rounded-lg">
                <AccordionTrigger className="px-4">Rang</AccordionTrigger>
                <AccordionContent className="px-4 pb-4 m-0">
                  <SalesRanksManager item={userItem} setUserItem={setUserItem} setReload={setReload} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="salesUntilNextRank" className="border rounded-lg">
                <AccordionTrigger className="px-4">Verkäufe</AccordionTrigger>
                <AccordionContent className="px-4 pb-4 m-0">
                  <SalesUntilNextRankManager item={userItem} setUserItem={setUserItem} setReload={setReload} />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="wordpressSalesPartner" className="border rounded-lg">
                <AccordionTrigger className="px-4">WordPress Vertriebspartner</AccordionTrigger>
                <AccordionContent className="px-4 pb-4 m-0">
                  <WordPressSalesPartnersManager item={userItem} setUserItem={setUserItem} setReload={setReload} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContainer>
          <div className="flex flex-col gap-4 w-full">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Vorname</TableCell>
                  <TableCell>{userItem.firstName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Nachname</TableCell>
                  <TableCell>{userItem.lastName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Email</TableCell>
                  <TableCell>{userItem.mail}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Rolle</TableCell>
                  <TableCell>{userItem.role}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Rang</TableCell>
                  <TableCell>
                    {userItem.selectableEntities.salesRanks.find(
                      (salesRank) => salesRank._id === userItem.belongsToSalesRankId
                    )?.value || "Kein Rang"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Zugewiesen an</TableCell>
                  <TableCell>
                    {userItem.belongsToUserId ? (
                      <Link
                        to={`/settings/users/${userItem.belongsToUserId}`}
                        className="text-primary hover:underline inline-flex items-center gap-2"
                      >
                        <User size={16} />
                        {userItem.selectableEntities.users.find((user) => user._id === userItem.belongsToUserId)
                          ?.value || "Kein Benutzer"}
                      </Link>
                    ) : (
                      "Kein Benutzer"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">WordPress Vertriebspartner</TableCell>
                  <TableCell>
                    {userItem.wordpressSalesPartnerId ? (
                      <>
                        {userItem.selectableEntities.salesPartners.find(
                          (partner) => partner.id === userItem.wordpressSalesPartnerId
                        )?.value || "Kein Vertriebspartner"}
                      </>
                    ) : (
                      "Kein Vertriebspartner"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Downline Hierarchie</TableCell>
                </TableRow>
                {userItem.downlineHierarchy.length > 0 ? (
                  userItem.downlineHierarchy.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <Link
                          to={`/settings/users/${user._id}`}
                          className="text-primary hover:underline inline-flex items-center gap-2"
                        >
                          <User size={16} />
                          {user.value} - {user.salesRankTitle}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell className="italic">Keine Vertriebspartner in der Downline</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Upline Hierarchie</TableCell>
                </TableRow>
                {userItem.uplineHierarchy.length > 0 ? (
                  userItem.uplineHierarchy.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <Link
                          to={`/settings/users/${user._id}`}
                          className="text-primary hover:underline inline-flex items-center gap-2"
                        >
                          <User size={16} />
                          {user.value} - {user.salesRankTitle}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell className="italic">Keine Vertriebspartner in der Upline</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </AccordionContainerParent>
      )}
    </div>
  );
};

const WordPressSalesPartnersManager = ({
  item,
  setUserItem,
  setReload,
}: {
  item: UserDetailsResponseDto;
  setUserItem: Dispatch<SetStateAction<UserDetailsResponseDto>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [selectedSalesPartner, setSelectedSalesPartner] = useState<string | undefined>(item.wordpressSalesPartnerId);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    const response = await apiRequest<UserResponseDto>(`settings/users/${item._id}/wordpress-sales-partner`, "PATCH", {
      body: {
        wordpressSalesPartnerId: selectedSalesPartner === "undefined" ? undefined : selectedSalesPartner,
      },
      toast: {
        toastText: "WordPress Vertriebspartner erfolgreich gespeichert",
      },
    });

    if (response.data) {
      setUserItem({
        ...item,
        wordpressSalesPartnerId: selectedSalesPartner === "undefined" ? undefined : selectedSalesPartner,
      });
      setReload((prev) => !prev);
      setIsEditing(false);
    }
  };

  if (!item.selectableEntities.salesPartners?.length) {
    return null;
  }

  return (
    <div className="">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <h3 className="font-medium">WordPress Vertriebspartner</h3>
          <Button onClick={() => setIsEditing(!isEditing)} variant="secondary" size="icon" className="self-end">
            {isEditing ? <X size={16} /> : <Pencil size={16} />}
          </Button>
        </div>
        {isEditing ? (
          <>
            <div className="flex flex-col gap-2">
              <Select value={selectedSalesPartner ?? "null"} onValueChange={(value) => setSelectedSalesPartner(value)}>
                <SelectTrigger>
                  <SelectValue>
                    {item.selectableEntities.salesPartners.find((partner) => partner.id === selectedSalesPartner)
                      ?.value || "Kein Partner"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="undefined">Kein Partner</SelectItem>
                  {item.selectableEntities.salesPartners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-row gap-2">
              <Button onClick={handleSave}>Speichern</Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <p>
              {item.selectableEntities.salesPartners.find((partner) => partner.id === item.wordpressSalesPartnerId)
                ?.value || "Kein Partner"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const SalesRanksManager = ({
  item,
  setUserItem,
  setReload,
}: {
  item: UserDetailsResponseDto;
  setUserItem: Dispatch<SetStateAction<UserDetailsResponseDto>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();

  const [selectedSalesRank, setSelectedSalesRank] = useState<string | null>(item.belongsToSalesRankId);
  const [selectedUser, setSelectedUser] = useState<string | null>(item.belongsToUserId);
  const [usersToSelect, setUsersToSelect] = useState<UserSelectableEntityResponseDto[]>(
    item.selectableEntities?.users || []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const getUsersToSelect = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<UserSelectableEntitiesResponseDto>(
      `settings/users/${item._id}/selectable-entities/${selectedSalesRank}`,
      "GET"
    );
    setIsLoading(false);
    if (response.data) {
      setUsersToSelect(response.data.users);
    }
  }, [apiRequest, item._id, selectedSalesRank]);

  useEffect(() => {
    if (selectedSalesRank && !initLoading && selectedSalesRank !== "null") {
      getUsersToSelect();
    } else {
      setInitLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUsersToSelect, selectedSalesRank]);

  const handleSave = async () => {
    if (selectedSalesRank === "null" || selectedSalesRank === null) {
      await handleRemoveRole();
      return;
    }

    const req: UpdateUserHierarchyRequestDto = {
      belongsToSalesRankId: selectedSalesRank,
      belongsToUserId: usersToSelect.length === 0 ? null : selectedUser === "null" ? null : selectedUser || null,
    };

    const response = await apiRequest<UserResponseDto>(`settings/users/${item._id}/hierarchy`, "PATCH", {
      body: req,
      toast: {
        toastText: "Rang erfolgreich gespeichert",
      },
    });
    if (response.data) {
      setUserItem({
        ...item,
        selectableEntities: {
          ...item.selectableEntities,
          salesRankUsed: selectedSalesRank,
        },
      });
      setReload((prev) => !prev);
    }
  };

  const handleRemoveRole = async () => {
    const response = await apiRequest<UserResponseDto>(`settings/users/${item._id}/hierarchy`, "DELETE", {
      toast: {
        toastText: "Rang erfolgreich gespeichert",
      },
    });
    if (response.data) {
      setUserItem({
        ...item,
        selectableEntities: {
          ...item.selectableEntities,
          salesRankUsed: "null",
        },
      });
      setReload((prev) => !prev);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between items-center">
        <h3 className="font-medium">Rang</h3>
        <Button onClick={() => setIsEditing(!isEditing)} variant="secondary" size="icon" className="self-end">
          {isEditing ? <X size={16} /> : <Pencil size={16} />}
        </Button>
      </div>
      {isEditing ? (
        <>
          <div className="flex flex-col gap-2">
            <Select
              value={selectedSalesRank ?? "null"}
              onValueChange={(value) => setSelectedSalesRank(value)}
              disabled={!item.selectableEntities.hierarchyUpdateable}
            >
              <SelectTrigger>
                <SelectValue>
                  {item.selectableEntities.salesRanks.find((salesRank) => salesRank._id === selectedSalesRank)?.value ||
                    "Kein Rang"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={"null"}>Kein Rang</SelectItem>
                {item.selectableEntities.salesRanks.map((salesRank) => (
                  <SelectItem key={salesRank._id} value={salesRank._id}>
                    {salesRank.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DataFetchParent
              isLoading={isLoading}
              data={usersToSelect}
              renderElement={(loadedUsersToSelect: UserSelectableEntityResponseDto[]) => {
                if (loadedUsersToSelect.length === 0 || selectedSalesRank === "null" || selectedSalesRank === null) {
                  return null;
                }
                return (
                  <div>
                    <UserSelect
                      users={loadedUsersToSelect}
                      selectedUser={selectedUser}
                      setSelectedUser={setSelectedUser}
                      disabled={!item.selectableEntities.hierarchyUpdateable}
                    />
                  </div>
                );
              }}
            />
          </div>
          <div className="flex flex-row gap-2">
            <Button
              variant="destructive"
              onClick={handleRemoveRole}
              disabled={!item.belongsToSalesRankId || !item.selectableEntities.hierarchyUpdateable}
            >
              Rolle entfernen
            </Button>
            <Button onClick={handleSave} disabled={!item.selectableEntities.hierarchyUpdateable}>
              Speichern
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-2">
          <p>
            {item.selectableEntities.salesRanks.find((salesRank) => salesRank._id === item.belongsToSalesRankId)
              ?.value || "Kein Rang"}{" "}
            -{" "}
            {item.belongsToUserId
              ? "Zugewiesen an " +
                  item.selectableEntities.users.find((user) => user._id === item.belongsToUserId)?.value ||
                "Kein Benutzer"
              : "Kein Benutzer"}
          </p>
        </div>
      )}
    </div>
  );
};

const UserSelect = ({
  users,
  selectedUser,
  setSelectedUser,
  disabled,
}: {
  users: UserSelectableEntityResponseDto[];
  selectedUser: string | null;
  setSelectedUser: Dispatch<SetStateAction<string | null>>;
  disabled: boolean;
}) => {
  return (
    <Select value={selectedUser ?? "null"} onValueChange={(value) => setSelectedUser(value)} disabled={disabled}>
      <SelectTrigger>
        <SelectValue>{users.find((user) => user._id === selectedUser)?.value || "Kein Benutzer"}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={"null"}>Kein Benutzer</SelectItem>
        {users.map((user) => (
          <SelectItem key={user._id} value={user._id}>
            {user.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const SalesUntilNextRankManager = ({
  item,
  setUserItem,
  setReload,
}: {
  item: UserDetailsResponseDto;
  setUserItem: Dispatch<SetStateAction<UserDetailsResponseDto>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [salesUntilNextRank, setSalesUntilNextRank] = useState<string>(item.salesUntilNextRank?.toString() || "-");
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    const response = await apiRequest<UserResponseDto>(`settings/users/${item._id}/sales-until-next-rank`, "PATCH", {
      body: {
        salesUntilNextRank: parseInt(salesUntilNextRank),
      },
      toast: {
        toastText: "Verkäufe bis zum nächsten Rang erfolgreich gespeichert",
      },
    });

    if (response.data) {
      setUserItem({
        ...item,
        salesUntilNextRank: parseInt(salesUntilNextRank),
      });
      setReload((prev) => !prev);
    }
  };

  if (!item.selectableEntities.hierarchyUpdateable) {
    return null;
  }

  return (
    <div className="">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <h3 className="font-medium">Verkäufe bis zum nächsten Rang</h3>
          <Button onClick={() => setIsEditing(!isEditing)} variant="secondary" size="icon" className="self-end">
            {isEditing ? <X size={16} /> : <Pencil size={16} />}
          </Button>
        </div>
        {isEditing ? (
          <>
            <div className="flex flex-col gap-2">
              <Input
                type="number"
                value={salesUntilNextRank}
                onChange={(e) => setSalesUntilNextRank(e.target.value)}
                min={1}
                disabled={!item.selectableEntities.hierarchyUpdateable}
              />
            </div>
            <div className="flex flex-row gap-2">
              <Button onClick={handleSave} disabled={!item.selectableEntities.hierarchyUpdateable}>
                Speichern
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <p>{item.salesUntilNextRank}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserItem;
